import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FiStar } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import CommonTable from '../components/CommonTable/CommonTable';
import EmptyState from '../components/EmptyState/EmptyState';
import { formatDateTimeToDDMMMYYYY } from '../../utils/dateFormatter';
import { getSavedViewMode, saveViewMode } from '../../utils/viewPersistence';
import { listForums } from '../../services/ForumService';
import axiosInstance from '../../services/AxiosInstance';
import { useForumAccess } from '../../hooks/useForumAccess';
import { buildQuerySuffix } from '../../utils/listQueryPersistence';
import { usePageToolbar } from '../layouts/nav/PageToolbarContext';
import '../../css/SearchFilterPanel.css';
import '../tasks/tasklist.css';
import '../Documents/commonstyle.css';
import './forum.css';

const STATUS_QUICK_FILTERS = [
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed', label: 'Closed' },
];

const EMPTY_PANEL_FILTERS = {
  show_closed: false,
  created_start: '',
  created_end: '',
  participant_id: '',
};

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const statusLabel = (s) => (s || '').replace('_', ' ');

const getPriorityBadgeClass = (priority) => {
  switch ((priority || '').toLowerCase()) {
    case 'high': return 'priority-badge priority-high';
    case 'medium': return 'priority-badge priority-medium';
    case 'low': return 'priority-badge priority-low';
    default: return 'priority-badge priority-medium';
  }
};

const MOBILE_LIST_MAX_WIDTH = 576;

const ForumList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasAccess, loading: accessLoading } = useForumAccess();
  const filterRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState(() => getSavedViewMode('forumlist', 'card'));
  const [searchDropDown, setSearchDropDown] = useState(false);
  const [selectedQuickFilter, setSelectedQuickFilter] = useState(() => searchParams.get('status') || '');
  const [searchText, setSearchText] = useState(() => searchParams.get('search') || '');
  const debouncedSearchText = useDebounce(searchText, 500);
  const [users, setUsers] = useState([]);
  const [panelFilters, setPanelFilters] = useState(() => ({
    show_closed: searchParams.get('show_closed') === 'true',
    created_start: searchParams.get('created_start') || '',
    created_end: searchParams.get('created_end') || '',
    participant_id: searchParams.get('participant_id') || '',
  }));
  const [filters, setFilters] = useState(() => ({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    show_closed: searchParams.get('show_closed') === 'true',
    created_start: searchParams.get('created_start') || '',
    created_end: searchParams.get('created_end') || '',
    participant_id: searchParams.get('participant_id') || '',
    page: Number(searchParams.get('page')) || 1,
    limit: 20,
  }));
  const hasInitialLoad = useRef(false);
  const prevDebouncedSearchRef = useRef('');
  const filterUrlSyncSkipRef = useRef(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_LIST_MAX_WIDTH
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_LIST_MAX_WIDTH);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const displayViewMode = isMobile ? 'card' : viewMode;

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (userData?.corporateId) {
      axiosInstance.get(`/list/corporateuserslist?corporateId=${userData.corporateId}`)
        .then((res) => setUsers(res.data.users || []))
        .catch(() => {});
    }
  }, []);

  const fetchForums = async (appliedFilters = filters) => {
    try {
      setLoading(true);
      const params = {
        search: appliedFilters.search || undefined,
        status: appliedFilters.status || undefined,
        show_closed: appliedFilters.show_closed ? 'true' : undefined,
        created_start: appliedFilters.created_start || undefined,
        created_end: appliedFilters.created_end || undefined,
        participant_id: appliedFilters.participant_id || undefined,
        page: appliedFilters.page,
        limit: appliedFilters.limit,
      };
      const res = await listForums(params);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load forums');
    } finally {
      setLoading(false);
    }
  };

  const getForumListQuerySuffix = (applied = filters) => buildQuerySuffix({
    search: applied.search || searchText || undefined,
    status: applied.status || undefined,
    show_closed: applied.show_closed ? 'true' : undefined,
    created_start: applied.created_start || undefined,
    created_end: applied.created_end || undefined,
    participant_id: applied.participant_id || undefined,
    page: applied.page && applied.page !== 1 ? String(applied.page) : undefined,
  });

  const commitForumListToUrl = (applied) => {
    filterUrlSyncSkipRef.current = true;
    const suffix = getForumListQuerySuffix(applied);
    setSearchParams(new URLSearchParams(suffix.replace(/^\?/, '')), { replace: true });
  };

  const applyFilters = (getNext) => {
    setFilters((prev) => {
      const next = typeof getNext === 'function' ? getNext(prev) : getNext;
      fetchForums(next);
      commitForumListToUrl(next);
      return next;
    });
  };

  useEffect(() => {
    if (!hasInitialLoad.current) {
      hasInitialLoad.current = true;
      prevDebouncedSearchRef.current = debouncedSearchText;
      fetchForums(filters);
      commitForumListToUrl(filters);
      return;
    }
    if (prevDebouncedSearchRef.current !== debouncedSearchText) {
      prevDebouncedSearchRef.current = debouncedSearchText;
      applyFilters((prev) => ({ ...prev, search: debouncedSearchText, page: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText]);

  // Restore filters when returning via history / Back with query
  useEffect(() => {
    if (filterUrlSyncSkipRef.current) {
      filterUrlSyncSkipRef.current = false;
      return;
    }
    if (!hasInitialLoad.current) return;

    const next = {
      search: searchParams.get('search') || '',
      status: searchParams.get('status') || '',
      show_closed: searchParams.get('show_closed') === 'true',
      created_start: searchParams.get('created_start') || '',
      created_end: searchParams.get('created_end') || '',
      participant_id: searchParams.get('participant_id') || '',
      page: Number(searchParams.get('page')) || 1,
      limit: 20,
    };
    setSearchText(next.search);
    setSelectedQuickFilter(next.status);
    setPanelFilters({
      show_closed: next.show_closed,
      created_start: next.created_start,
      created_end: next.created_end,
      participant_id: next.participant_id,
    });
    setFilters(next);
    fetchForums(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (searchDropDown) {
      setPanelFilters({
        show_closed: filters.show_closed,
        created_start: filters.created_start,
        created_end: filters.created_end,
        participant_id: filters.participant_id,
      });
      setSelectedQuickFilter(filters.status);
    }
  }, [searchDropDown, filters.status, filters.show_closed, filters.created_start,
    filters.created_end, filters.participant_id]);

  const handlePanelChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPanelFilters((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleQuickFilterClick = (value) => {
    setSelectedQuickFilter((prev) => (prev === value ? '' : value));
  };

  const handleSearch = () => {
    applyFilters((prev) => ({
      ...prev,
      ...panelFilters,
      status: selectedQuickFilter,
      page: 1,
    }));
    setSearchDropDown(false);
  };

  const resetFilters = () => {
    setPanelFilters(EMPTY_PANEL_FILTERS);
    setSelectedQuickFilter('');
    applyFilters((prev) => ({
      ...prev,
      ...EMPTY_PANEL_FILTERS,
      status: '',
      page: 1,
    }));
    setSearchDropDown(false);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    saveViewMode('forumlist', mode);
  };

  const openForum = (id) => navigate(`/forum/detail/${id}${getForumListQuerySuffix()}`);

  // Keep create link aware of current list filters for Back
  const createForumPath = `/forum/create${getForumListQuerySuffix()}`;

  const tableColumns = [
    {
      key: 'title',
      header: 'Title',
      render: (row) => (
        <span>
          {row.is_favorite && <FiStar className="text-warning me-1" />}
          {row.title}
        </span>
      ),
    },
    { key: 'creator', header: 'Creator', render: (row) => row.creator?.full_name || '—' },
    {
      key: 'created_at',
      header: 'Created',
      render: (row) => formatDateTimeToDDMMMYYYY(row.created_at, true),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span className={`forum-status-badge forum-status-${row.status}`}>{statusLabel(row.status)}</span>
      ),
    },
    { key: 'last_comment_by', header: 'Last comment by', render: (row) => row.last_comment_by || '—' },
    {
      key: 'priority',
      header: 'Priority',
      render: (row) => (
        <span className={getPriorityBadgeClass(row.priority)}>{row.priority || 'Medium'}</span>
      ),
    },
  ];

  const forumToolbar = useMemo(
    () => {
      if (accessLoading || !hasAccess) return null;
      return (
        <>
          <div className="view-toggle-container forum-list-view-toggle">
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('card')}
              title="Card view"
              aria-label="Card view"
            >
              <i className="fas fa-th-large"></i>
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('list')}
              title="List view"
              aria-label="List view"
            >
              <i className="fas fa-list"></i>
            </button>
          </div>
          <button
            type="button"
            className="btn forum-list-filter-btn header-toolbar-btn"
            onClick={() => setSearchDropDown((v) => !v)}
            title="Filters"
            aria-label="Filters"
          >
            <i className="fas fa-filter"></i>
          </button>
        </>
      );
    },
    [accessLoading, hasAccess, viewMode]
  );

  usePageToolbar(forumToolbar, [forumToolbar]);

  if (accessLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  return (
    <div className="task-list-container">
      {searchDropDown && (
        <div className="filter-wrapper" ref={filterRef}>
          <div className="content">
            <div className="quick-filters">
              {STATUS_QUICK_FILTERS.map((filter) => (
                <div
                  key={filter.value}
                  className={`quick-filter ${selectedQuickFilter === filter.value ? 'active' : ''}`}
                  onClick={() => handleQuickFilterClick(filter.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleQuickFilterClick(filter.value)}
                  role="button"
                  tabIndex={0}
                >
                  {filter.label}
                </div>
              ))}
            </div>

            <div className="main-filters">
              <div className="filter-form">
                <div className="filter-row">
                  <div className="filter-group">
                    <label>Participant</label>
                    <select
                      name="participant_id"
                      value={panelFilters.participant_id}
                      onChange={handlePanelChange}
                    >
                      <option value="">All participants</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.full_name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="filter-row">
                  <div className="filter-group">
                    <label>Created from</label>
                    <input
                      type="date"
                      name="created_start"
                      className="form-control"
                      value={panelFilters.created_start}
                      onChange={handlePanelChange}
                    />
                  </div>

                  <div className="filter-group">
                    <label>Created to</label>
                    <input
                      type="date"
                      name="created_end"
                      className="form-control"
                      value={panelFilters.created_end}
                      onChange={handlePanelChange}
                    />
                  </div>
                </div>

                <div className="checkbox-filter-row">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="show_closed"
                      checked={panelFilters.show_closed}
                      onChange={handlePanelChange}
                      className="checkbox-input"
                    />
                    <span>Include closed</span>
                  </label>
                </div>
              </div>

              <div className="filter-actions">
                <button type="button" className="search-btn" onClick={handleSearch}>
                  <i className="fas fa-search me-2"></i>
                  Search
                </button>
                <button type="button" className="reset-btn" onClick={resetFilters}>
                  <i className="fas fa-undo me-2"></i>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      ) : displayViewMode === 'list' ? (
        <CommonTable
          columns={tableColumns}
          data={items}
          onRowClick={(row) => openForum(row.id)}
          emptyMessage="No discussion forums found"
          emptyStateIcon="fas fa-comments"
          getRowId={(row) => row.id}
          maxHeight="85vh"
          minHeight="530px"
        />
      ) : (
        <div className="row row-list">
          {items.length === 0 ? (
            <div className="col-12">
              <EmptyState
                icon="fas fa-comments"
                title="No Data Found"
                description="No discussion forums found"
              />
            </div>
          ) : (
            items.map((forum) => (
              <div className="col-xl-3 col-xxl-4 col-lg-6 col-md-6 col-sm-6 mb-4" key={forum.id}>
                <div
                  className={`task-card ${forum.is_favorite ? 'forum-card-favorited' : ''}`}
                  onClick={() => openForum(forum.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && openForum(forum.id)}
                >
                  <div className="task-card-header">
                    <div>
                      <div className="task-id">#{forum.id}</div>
                      <h6 className="task-title">
                        {forum.is_favorite && <FiStar className="text-warning me-1" />}
                        {forum.title}
                      </h6>
                      <div className="task-meta">
                        <i className="fas fa-calendar-alt"></i>
                        <span>Created on {formatDateTimeToDDMMMYYYY(forum.created_at, true)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="task-card-body">
                    <ul className="task-info-list">
                      <li className="task-info-item">
                        <span className="task-info-label">Priority</span>
                        <span className={getPriorityBadgeClass(forum.priority)}>
                          {forum.priority || 'Medium'}
                        </span>
                      </li>
                      <li className="task-info-item">
                        <span className="task-info-label">Status</span>
                        <span className={`forum-status-badge forum-status-${forum.status}`}>
                          {statusLabel(forum.status)}
                        </span>
                      </li>
                      <li className="task-info-item">
                        <span className="task-info-label">Creator</span>
                        <span>{forum.creator?.full_name || '—'}</span>
                      </li>
                    </ul>
                    {forum.last_comment_by && (
                      <small className="text-muted d-block mt-2">
                        Last comment by {forum.last_comment_by}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {total > filters.limit && (
        <div className="d-flex justify-content-center gap-2 mt-3">
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={filters.page <= 1}
            onClick={() => applyFilters((f) => ({ ...f, page: f.page - 1 }))}
          >
            Previous
          </Button>
          <span className="align-self-center small">Page {filters.page}</span>
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={filters.page * filters.limit >= total}
            onClick={() => applyFilters((f) => ({ ...f, page: f.page + 1 }))}
          >
            Next
          </Button>
        </div>
      )}

      <BackToTop />
      <InternalFooter />
    </div>
  );
};

export default ForumList;
